import React from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { ArrowRight, Building, Shield, Users, Database, Globe, Zap, CheckCircle, Calendar, Clock, Award } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navigation from './Navigation';
import tulioIcon from './assets/tulio.png'

const GovernmentPage = () => {
  const governmentServices = [
    {
      id: 'digital-services',
      title: 'Digital Citizen Services',
      description: 'Modern, user-friendly portals for citizen-government interactions',
      icon: Users,
      color: 'from-indigo-500 to-blue-600',
      pricing: 'Custom pricing',
      timeline: '6-12 months',
      features: ['Citizen portals', 'Online applications', 'Document management', 'Multi-language support']
    },
    {
      id: 'data-integration',
      title: 'Government Data Integration',
      description: 'Unify data across departments for better decision making',
      icon: Database,
      color: 'from-green-500 to-teal-600',
      pricing: 'Starting $200k',
      timeline: '8-15 months',
      features: ['Inter-agency data sharing', 'Real-time analytics', 'Business intelligence', 'Data governance']
    },
    {
      id: 'modernization',
      title: 'Legacy System Modernization',
      description: 'Transform outdated systems with modern, secure architecture',
      icon: Zap,
      color: 'from-orange-500 to-red-600',
      pricing: 'Starting $300k',
      timeline: '12-24 months',
      features: ['Cloud migration', 'API development', 'Security upgrades', 'Performance optimization']
    },
    {
      id: 'cybersecurity',
      title: 'Cybersecurity & Compliance',
      description: 'Protect sensitive government data and ensure regulatory compliance',
      icon: Shield,
      color: 'from-red-500 to-pink-600',
      pricing: 'Starting $150k',
      timeline: '4-8 months',
      features: ['Security audits', 'Compliance frameworks', 'Threat monitoring', 'Incident response']
    }
  ];

  const governmentBenefits = [
    {
      title: 'Proven Government Experience',
      description: 'Extensive experience working with government agencies and public sector requirements',
      icon: Building,
      stats: 'GovTech SG Partner'
    },
    {
      title: 'Security & Compliance',
      description: 'Deep understanding of government security standards and regulatory requirements',
      icon: Shield,
      stats: '100% Compliance Record'
    },
    {
      title: 'Citizen-Centric Design',
      description: 'Focus on improving citizen experience and accessibility in all solutions',
      icon: Users,
      stats: '85% Citizen Satisfaction'
    }
  ];

  const complianceStandards = [
    { name: 'FedRAMP', description: 'Federal cloud security standards' },
    { name: 'FISMA', description: 'Information security management' },
    { name: 'Section 508', description: 'Accessibility compliance' },
    { name: 'GDPR', description: 'Data protection regulation' },
    { name: 'ISO 27001', description: 'Information security management' },
    { name: 'SOC 2', description: 'Security and availability controls' }
  ];

  const governmentSolutions = [
    {
      category: 'Citizen Services',
      solutions: [
        'Online permit and license applications',
        'Tax filing and payment systems',
        'Healthcare service portals',
        'Education management systems',
        'Voting and election systems'
      ]
    },
    {
      category: 'Internal Operations',
      solutions: [
        'Inter-department collaboration tools',
        'Document management systems',
        'Workflow automation platforms',
        'Employee self-service portals',
        'Budget and financial management'
      ]
    },
    {
      category: 'Data & Analytics',
      solutions: [
        'Population analytics dashboards',
        'Economic development tracking',
        'Public safety analytics',
        'Infrastructure monitoring',
        'Performance measurement systems'
      ]
    }
  ];

  const caseStudies = [
    {
      agency: 'GovTech Singapore',
      project: 'Digital Services Platform',
      challenge: 'Modernize citizen-facing services across multiple agencies',
      result: '50% faster service delivery',
      impact: '2M+ citizens served',
      timeline: '18 months'
    },
    {
      agency: 'Public Healthcare Agency',
      project: 'Patient Data Integration',
      challenge: 'Unify patient records across healthcare facilities',
      result: '30% improvement in care coordination',
      impact: '500K+ patient records integrated',
      timeline: '12 months'
    },
    {
      agency: 'Transportation Authority',
      project: 'Smart City Infrastructure',
      challenge: 'Real-time traffic monitoring and optimization',
      result: '25% reduction in traffic congestion',
      impact: 'City-wide implementation',
      timeline: '15 months'
    }
  ];

  const partnerships = [
    {
      name: 'GovTech Singapore',
      type: 'Technology Partner',
      description: 'Certified partner for government digital transformation projects'
    },
    {
      name: 'A*STAR',
      type: 'Research Collaboration',
      description: 'Joint research and development initiatives'
    },
    {
      name: 'IMDA',
      type: 'Digital Innovation',
      description: 'Collaborative projects for digital government initiatives'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-3">
              <img 
               src={tulioIcon} 
               alt="Tulio Labs" 
               className="h-12 w-auto" 
             />
            </Link>
            <Navigation />
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-6 bg-indigo-100 text-indigo-700 hover:bg-indigo-100">Government Solutions</Badge>
              <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6">
                Modernizing
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-blue-600">
                  Public Services
                </span>
              </h1>
              <p className="text-xl text-slate-600 mb-8 leading-relaxed">
                Transform government operations with secure, compliant, and citizen-centric digital solutions that improve public service delivery and operational efficiency.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Button 
                  size="lg" 
                  className="bg-indigo-600 hover:bg-indigo-700"
                  onClick={() => window.open('https://calendly.com/admin-tuliolabs/30min', '_blank')}
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Schedule Government Consultation
                </Button>
                <Link to="/case-studies">
                  <Button size="lg" variant="outline">
                    View Government Cases
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
              
              <div className="flex items-center space-x-8 text-slate-600">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-indigo-500" />
                  <span className="text-sm">GovTech Certified</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-indigo-500" />
                  <span className="text-sm">Security Compliant</span>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-gradient-to-br from-indigo-600 to-blue-700 rounded-2xl p-8 text-white">
                <h3 className="text-2xl font-bold mb-6">Government Impact</h3>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <div className="text-3xl font-bold">2M+</div>
                    <div className="text-sm opacity-90">Citizens Served</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold">85%</div>
                    <div className="text-sm opacity-90">Satisfaction Rate</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold">50%</div>
                    <div className="text-sm opacity-90">Faster Services</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold">15+</div>
                    <div className="text-sm opacity-90">Agencies Served</div>
                  </div>
                </div>
              </div>
              
              <div className="absolute -bottom-6 -right-6 bg-white rounded-xl shadow-lg p-4 border">
                <div className="flex items-center space-x-3">
                  <Award className="w-8 h-8 text-indigo-600" />
                  <div>
                    <div className="font-semibold text-slate-900">GovTech Partner</div>
                    <div className="text-sm text-slate-600">Certified Provider</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Government Benefits */}
      <section className="py-16 px-4 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">Why Government Agencies Trust Us</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {governmentBenefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                  <CardContent className="p-8">
                    <div className="w-16 h-16 bg-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                      <Icon className="w-8 h-8 text-indigo-600" />
                    </div>
                    <h3 className="text-xl font-bold mb-4">{benefit.title}</h3>
                    <p className="text-slate-600 mb-4">{benefit.description}</p>
                    <div className="text-lg font-bold text-indigo-600">{benefit.stats}</div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Government Services */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Government Technology Services</h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Comprehensive solutions designed for public sector requirements, security standards, and citizen needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {governmentServices.map((service) => {
              const Icon = service.icon;
              return (
                <Card key={service.id} className="group hover:shadow-xl transition-all duration-300 border-slate-200 hover:border-indigo-200">
                  <CardContent className="p-8">
                    <div className={`w-12 h-12 bg-gradient-to-br ${service.color} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-4">{service.title}</h3>
                    <p className="text-slate-600 mb-6">{service.description}</p>
                    
                    <div className="flex justify-between items-center mb-6 text-sm">
                      <div className="text-slate-600">
                        <strong>Investment:</strong> {service.pricing}
                      </div>
                      <div className="flex items-center text-slate-600">
                        <Clock className="w-4 h-4 mr-1" />
                        {service.timeline}
                      </div>
                    </div>
                    
                    <ul className="space-y-2 mb-6">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center text-sm text-slate-600">
                          <CheckCircle className="w-4 h-4 text-indigo-500 mr-2 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    
                    <Button className="w-full bg-slate-900 hover:bg-slate-800">
                      Request Information
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Government Solutions */}
      <section className="py-20 px-4 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Government Solution Areas</h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Comprehensive technology solutions across all areas of government operations and citizen services.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {governmentSolutions.map((category, index) => (
              <Card key={index} className="h-full">
                <CardContent className="p-8">
                  <h3 className="text-xl font-bold mb-6 text-center">{category.category}</h3>
                  <ul className="space-y-3">
                    {category.solutions.map((solution, idx) => (
                      <li key={idx} className="flex items-start text-sm">
                        <CheckCircle className="w-4 h-4 text-indigo-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-700">{solution}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance Standards */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Security & Compliance Standards</h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              We adhere to the highest security and compliance standards required for government projects.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {complianceStandards.map((standard, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Shield className="w-6 h-6 text-indigo-600" />
                  </div>
                  <h3 className="font-bold text-lg mb-2">{standard.name}</h3>
                  <p className="text-slate-600 text-sm">{standard.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="py-20 px-4 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Government Success Stories</h2>
            <p className="text-xl text-slate-600">Real impact for public sector organizations</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {caseStudies.map((study, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <Badge className="mb-4 bg-indigo-100 text-indigo-700">Government</Badge>
                  <h3 className="text-lg font-bold mb-2">{study.agency}</h3>
                  <h4 className="text-md font-semibold text-indigo-600 mb-3">{study.project}</h4>
                  <p className="text-slate-600 text-sm mb-4">{study.challenge}</p>
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-600">Key Result:</span>
                      <span className="font-semibold text-indigo-600">{study.result}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-600">Impact:</span>
                      <span className="font-semibold text-emerald-600">{study.impact}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center text-sm text-slate-500">
                    <Clock className="w-4 h-4 mr-1" />
                    {study.timeline} delivery
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Government Partnerships */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Government Partnerships</h2>
            <p className="text-xl text-slate-600">Trusted partnerships with key government agencies and organizations</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {partnerships.map((partnership, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Building className="w-8 h-8 text-indigo-600" />
                  </div>
                  <h3 className="text-lg font-bold mb-2">{partnership.name}</h3>
                  <Badge className="mb-4" variant="secondary">{partnership.type}</Badge>
                  <p className="text-slate-600 text-sm">{partnership.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Modernize Your Government Services?</h2>
          <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
            Partner with us to deliver secure, compliant, and citizen-centric digital solutions that improve public service delivery.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              size="lg" 
              className="bg-indigo-600 hover:bg-indigo-700 px-8 py-4"
              onClick={() => window.open('https://calendly.com/admin-tuliolabs/30min', '_blank')}
            >
              <Calendar className="w-5 h-5 mr-2" />
              Schedule Government Consultation
            </Button>
            <Link to="/services">
              <Button size="lg" variant="outline" className="border-slate-400 text-white hover:bg-white/10 px-8 py-4">
                Explore Government Services
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
          
          <div className="mt-8 flex items-center justify-center space-x-8 text-slate-400">
            <div className="flex items-center space-x-2">
              <Shield className="w-4 h-4" />
              <span className="text-sm">Security Compliant</span>
            </div>
            <div className="flex items-center space-x-2">
              <Award className="w-4 h-4" />
              <span className="text-sm">GovTech Certified</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <Link to="/" className="flex items-center space-x-3 mb-6 md:mb-0">
              <img 
  src={tulioIcon} 
  alt="Tulio Labs" 
  className="h-12 w-auto" 
/>
            </Link>
            
            <div className="flex items-center space-x-8 text-sm text-slate-400">
              <Link to="/services" className="hover:text-white transition-colors">Services</Link>
              <Link to="/government" className="hover:text-white transition-colors">Government</Link>
              <Link to="/case-studies" className="hover:text-white transition-colors">Case Studies</Link>
              <span>© 2025 Tulio Labs</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default GovernmentPage;